import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { RefusalTag } from "@/components/Badges";
import { api, formatApiError } from "@/lib/api";
import { SOURCE_LABELS, formatDate } from "@/lib/constants";

const Field = ({ label, value, href, testId }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 border-b border-border py-2 text-sm last:border-0">
    <div className="eyebrow pt-0.5 !text-[10px]">{label}</div>
    <div data-testid={testId} className="min-w-0 break-words">
      {value ? (href ? <a href={href} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline">{value}</a> : value) : <span className="text-muted-foreground">—</span>}
    </div>
  </div>
);

const Block = ({ title, children }) => (
  <div className="mt-6">
    <div className="eyebrow mb-2">{title}</div>
    <div className="rounded-md border border-border px-4">{children}</div>
  </div>
);

const ZefixBlock = ({ z }) => (
  <Block title="Registre du Commerce (Zefix)">
    <Field label="Raison RC" value={z.name} />
    <Field label="UID" value={z.uid} href={z.excerpt_url} />
    <Field label="Statut" value={z.status} testId="lead-detail-legal-status" />
    <Field label="Forme" value={z.legal_form} />
    <Field label="Siège" value={z.legal_seat} />
    <Field label="But" value={z.purpose} />
    <Field label="Personnes" value={(z.people || []).map((p) => `${p.name}${p.role ? ` (${p.role})` : ""}`).join(" · ")} />
    <Field label="Fiabilité" value={z.match_score != null ? `${Math.round(z.match_score * 100)} %` : null} />
  </Block>
);

export const LeadDetailSheet = ({ leadId, onClose }) => {
  const queryClient = useQueryClient();
  const { data: lead } = useQuery({ queryKey: ["lead", leadId], queryFn: () => api.get(`/leads/${leadId}`).then((r) => r.data), enabled: !!leadId });

  const remove = async () => {
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead supprimé");
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="lead-detail-sheet" className="w-full overflow-y-auto sm:max-w-xl">
        {lead && (
          <>
            <SheetHeader>
              <div className="eyebrow">{lead.category || "Entreprise"}</div>
              <SheetTitle className="font-display text-2xl font-black tracking-tight">{lead.name}</SheetTitle>
              {lead.do_not_call && <div><RefusalTag testId="lead-detail-refusal-tag" /></div>}
            </SheetHeader>
            <Block title="Coordonnées">
              <Field label="Adresse" value={[lead.address, [lead.npa, lead.city].filter(Boolean).join(" "), lead.canton].filter(Boolean).join(", ")} />
              <Field label="Téléphone" value={lead.phone} testId="lead-detail-phone" />
              <Field label="Mobile" value={lead.mobile} />
              <Field label="E-mail" value={lead.email} testId="lead-detail-email" />
              <Field label="Autres e-mails" value={(lead.emails || []).filter((e) => e !== lead.email).join(", ")} />
              <Field label="Site web" value={lead.website} href={lead.website} />
              <Field label="Formulaire" value={lead.contact_form} href={lead.contact_form} />
              <Field label="Dirigeant" value={lead.dirigeant} testId="lead-detail-leader" />
              <Field label="LinkedIn" value={lead.linkedin} href={lead.linkedin} />
              <Field label="Facebook" value={lead.facebook} href={lead.facebook} />
              <Field label="Instagram" value={lead.instagram} href={lead.instagram} />
              <Field label="Note" value={lead.rating ? `${lead.rating} / 5 (${lead.rating_count || 0} avis)` : null} />
              <Field label="GPS" value={lead.lat ? `${lead.lat.toFixed(5)}, ${lead.lng.toFixed(5)}` : null} href={lead.lat ? `https://www.google.com/maps?q=${lead.lat},${lead.lng}` : null} />
            </Block>
            {lead.zefix && <ZefixBlock z={lead.zefix} />}
            {lead.crawl && (
              <Block title="Cloud Crawler">
                <Field label="Pages" value={(lead.crawl.pages || []).join(" · ")} />
                <Field label="E-mails" value={(lead.crawl.emails || []).join(", ")} />
                <Field label="Erreur" value={lead.crawl.error} />
              </Block>
            )}
            {lead.enrichment && (
              <Block title="Enrichissement API">
                <Field label="Fournisseur" value={lead.enrichment.provider} />
                <Field label="E-mail" value={lead.enrichment.email} />
                <Field label="Score" value={lead.enrichment.score} />
              </Block>
            )}
            <Block title="Provenance">
              {Object.entries(lead.raw || {}).map(([src, raw]) => (
                <Field key={src} label={SOURCE_LABELS[src] || src} value={raw?.url || raw?.maps_url || "fiche"} href={raw?.url || raw?.maps_url} />
              ))}
              <Field label="Créé" value={formatDate(lead.created_at)} />
              <Field label="Mis à jour" value={formatDate(lead.updated_at)} />
            </Block>
            <div className="mt-6 flex gap-2">
              {lead.website && <Button asChild variant="outline" size="sm"><a href={lead.website} target="_blank" rel="noreferrer"><ExternalLink className="mr-1.5 h-3.5 w-3.5" />Ouvrir le site</a></Button>}
              <Button variant="outline" size="sm" data-testid="lead-delete-button" onClick={remove} className="text-[#FF3B30] hover:text-[#FF3B30]"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Supprimer</Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
